import type { BrandColor } from '~kui/types/kui';

/**
 * Brand colours that have a token → tint gradient pairing used by the unified
 * card surfaces (self discovery, industry stats, occupation CTA).
 */
export type GradientColor = Extract<BrandColor, 'lime' | 'coral' | 'pink' | 'purple' | 'orange' | 'blue' | 'yellow'>;

const tint = (token: string) => `color-mix(in srgb, var(${token}) 60%, white)`;

/**
 * The `from`/`mid` colour stops for each brand gradient. `from` is the brand token that anchors
 * the gradient corner, `mid` its 60% tint that it fades through before
 * reaching the surface colour.
 */
export const BRAND_GRADIENT_COLORS: Record<GradientColor, { from: string; mid: string }> = {
  lime: { from: 'var(--secondary)', mid: tint('--secondary') },
  coral: { from: 'var(--brand-coral)', mid: tint('--brand-coral') },
  pink: { from: 'var(--brand-pink)', mid: tint('--brand-pink') },
  purple: { from: 'var(--brand-purple)', mid: tint('--brand-purple') },
  orange: { from: 'var(--brand-orange)', mid: tint('--brand-orange') },
  blue: { from: 'var(--brand-blue)', mid: tint('--brand-blue') },
  yellow: { from: 'var(--brand-yellow)', mid: tint('--brand-yellow') },
};

interface BrandRadialGradientOptions {
  /** Gradient origin/shape, e.g. `'circle at 100% 0%'`. */
  position?: string;
  /** Colour-stop positions for `[from, mid, end]`. */
  stops?: [from: string, mid: string, end: string];
  /** Colour the gradient fades out to (the card surface). */
  end?: string;
}

/**
 * Build a `radial-gradient(...)` string from a brand colour. The geometry
 * (position, stops, end colour) is configurable so each surface keeps its own
 * look while sharing the single source of brand colour pairings above.
 */
export const brandRadialGradient = (
  color: GradientColor,
  {
    position = 'circle at 100% 0%',
    stops = ['13%', '22%', '36%'],
    end = 'var(--card)',
  }: BrandRadialGradientOptions = {},
): string => {
  const { from, mid } = BRAND_GRADIENT_COLORS[color];
  const [fromStop, midStop, endStop] = stops;

  return `radial-gradient(${position}, ${from} ${fromStop}, ${mid} ${midStop}, ${end} ${endStop})`;
};
